import { closeActivity, type IActivity } from "@/api/Activities";
import { PlainButton } from "@medoma/components/Button/Button";
import CalendarDeleteIcon from "@medoma/components/icons/CalendarDeleteIcon";
import { activityStatusSchema } from "@medoma/models/activities";
import { Trans, useLingui } from "@lingui/react/macro";
import { Text } from "@medoma/components/Text/Text";
import { useMutation } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useDialog } from "@medoma/components/Dialog/useDialog";

const CloseActivitySeriesButton = ({
  id,
  status,
  onSuccess,
}: Pick<IActivity, "id" | "status"> & {
  onSuccess: () => Promise<void>;
}) => {
  const { t } = useLingui();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { confirm } = useDialog();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ id }: Pick<IActivity, "id">) => closeActivity(id),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att stänga aktiviteten.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await onSuccess();
    },
  });

  return (
    <>
      {status === activityStatusSchema.enum.active ? (
        <PlainButton
          size="small"
          weight="light"
          onClick={async () => {
            const confirmed = await confirm({
              title: t`Avsluta hela serien`,
              message: t`Alla framtida aktivitetstillfällen kommer att tas bort. Åtgärden går inte att ångra.`,
            });
            if (confirmed) {
              mutate({ id });
            }
          }}
          disabled={isPending}
        >
          <CalendarDeleteIcon />
          <Trans>Avsluta hela serien</Trans>
        </PlainButton>
      ) : (
        <Text element="div" size="small">
          <Trans>Aktivitetsserien är avslutad</Trans>
        </Text>
      )}
    </>
  );
};

export { CloseActivitySeriesButton };
